import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import { SearchInput } from "@src/components/SearchInput";
import { useForm } from "react-hook-form";
import { theme } from "@src/theme";
import MultiSelect from "@src/components/MultiSelect";
import { IPlaylistRecommendationTag } from "@src/types/playlistRecommendationTag";
import {
  useFetchPlaylistRecommendationTagsInfiniteQuery,
  useCreatePlaylistRecommendationTagMutation,
} from "@src/queries/playlistRecommendationTagsQueries";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { IPlaylistRecord } from "@src/types/playlist";
import { useNotification } from "@src/context/NotificationContext";
import useModal from "@src/hooks/useModal";
import Spinner from "@src/components/Spinner";

const ModalContentWrapper = styled.div`
  max-height: 60vh;
  overflow-y: auto;
`;

const SearchBarContainer = styled.div`
  position: sticky;
  top: 0;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  z-index: 1;
  padding-bottom: ${({ theme }): string => theme.margins.medium};
`;

type UpdatePlaylistRecommendationTagsProps = {
  onConfirm: (recommendationTagIds: string[]) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
  playlistData: IPlaylistRecord | null;
  isLoadingPlaylistDetails?: boolean;
};

const UpdatePlaylistRecommendationTags: React.FC<
  UpdatePlaylistRecommendationTagsProps
> = ({
  onConfirm,
  onClose,
  isOpen,
  loading,
  playlistData,
  isLoadingPlaylistDetails = false,
}) => {
  const { addNotification } = useNotification();
  const [selectedTags, setSelectedTags] = useState<
    IPlaylistRecommendationTag[]
  >([]);
  const playlistTagsIdsRef = useRef<string[] | null>(null);
  const isInitializingRef = useRef<boolean>(false);
  const userInteractedRef = useRef<boolean>(false);
  const { register, watch } = useForm({
    defaultValues: { searchText: "" },
  });
  const searchText = useDebounce<string>(watch("searchText"), 600);
  const {
    data: tagsResponse,
    isLoading: isTagsLoading,
    isError: isTagsError,
    error: tagsError,
    isFetching: isTagsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchTags,
  } = useFetchPlaylistRecommendationTagsInfiniteQuery({
    searchText,
  });

  const createTagMutation = useCreatePlaylistRecommendationTagMutation();
  const { isOpen: isCreateModalOpen, openModal, closeModal } = useModal();
  const [newTagTitle, setNewTagTitle] = useState("");

  const memorizedTags = useMemo(() => {
    const allTags =
      (
        (tagsResponse?.pages as AxiosResponse<
          ApiResponse<IPlaylistRecommendationTag[]>
        >[]) ?? []
      ).flatMap((response) => response.data.data) || [];
    return allTags;
  }, [tagsResponse]);

  const optionsEndRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!isOpen) return;

    let observer: IntersectionObserver | null = null;

    // Wait for the DOM to update and the ref to be available
    const timeoutId = setTimeout(() => {
      if (!optionsEndRef.current) return;

      observer = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      });

      const currentRef = optionsEndRef.current;
      observer.observe(currentRef);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer && optionsEndRef.current) {
        observer.unobserve(optionsEndRef.current);
      }
    };
  }, [isOpen, handleObserver, searchText, memorizedTags.length]);

  // Reset flags when modal closes
  useEffect(() => {
    if (!isOpen) {
      playlistTagsIdsRef.current = null;
      isInitializingRef.current = false;
      userInteractedRef.current = false;
      setSelectedTags([]);
    }
  }, [isOpen]);

  // Initialize selection only when modal opens or playlist tags change
  useEffect(() => {
    if (!isOpen || isLoadingPlaylistDetails || userInteractedRef.current)
      return;

    const playlistTags =
      playlistData?.playlistRecommendationTags &&
      Array.isArray(playlistData.playlistRecommendationTags)
        ? playlistData.playlistRecommendationTags
        : [];
    const currentPlaylistTagIds = playlistTags
      .map((tag: IPlaylistRecommendationTag) => tag.documentId)
      .sort();

    // Only initialize if playlist tags changed or it's the first time opening
    const currentIdsString = currentPlaylistTagIds.join(",");
    const previousIdsString = playlistTagsIdsRef.current?.join(",") || "";

    // If playlist tags changed, update the ref and initialize selection
    if (currentIdsString !== previousIdsString) {
      isInitializingRef.current = true;
      playlistTagsIdsRef.current = currentPlaylistTagIds;

      // Use tags from memorizedTags when available, otherwise use playlist tags
      // This ensures checkboxes are checked correctly (same object references)
      const normalizedTags = playlistTags.map((playlistTag) => {
        const foundInMemorized = memorizedTags.find(
          (mTag) => mTag.documentId === playlistTag.documentId
        );
        return foundInMemorized || playlistTag;
      });

      const timeoutId = setTimeout(() => {
        setSelectedTags(normalizedTags);
        isInitializingRef.current = false;
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    playlistData?.playlistRecommendationTags,
    isOpen,
    isLoadingPlaylistDetails,
    memorizedTags,
  ]);

  // Update selectedTags to use tags from memorizedTags when available
  // This ensures checkboxes are checked correctly (same object references)
  useEffect(() => {
    if (
      !isOpen ||
      isLoadingPlaylistDetails ||
      userInteractedRef.current ||
      !playlistTagsIdsRef.current
    )
      return;

    const playlistTags =
      playlistData?.playlistRecommendationTags &&
      Array.isArray(playlistData.playlistRecommendationTags)
        ? playlistData.playlistRecommendationTags
        : [];

    if (playlistTags.length > 0) {
      // Merge: use tags from memorizedTags when available, otherwise keep playlist tags
      const updatedTags = playlistTags.map((playlistTag) => {
        const foundInMemorized = memorizedTags.find(
          (mTag) => mTag.documentId === playlistTag.documentId
        );
        return foundInMemorized || playlistTag;
      });

      setSelectedTags((prevTags) => {
        // Check if we need to update (different object references)
        const prevIds = prevTags
          .map((tag) => tag.documentId)
          .sort()
          .join(",");
        const updatedIds = updatedTags
          .map((tag) => tag.documentId)
          .sort()
          .join(",");

        if (prevIds !== updatedIds) {
          return updatedTags;
        }

        // Check if object references have changed
        const needsUpdate = prevTags.some((prevTag) => {
          const updatedTag = updatedTags.find(
            (tag) => tag.documentId === prevTag.documentId
          );
          return updatedTag && updatedTag !== prevTag;
        });

        return needsUpdate ? updatedTags : prevTags;
      });
    }
  }, [
    memorizedTags,
    isOpen,
    isLoadingPlaylistDetails,
    playlistData?.playlistRecommendationTags,
  ]);

  const checkIfTagsChanged = () => {
    if (playlistData?.playlistRecommendationTags) {
      const tags = Array.isArray(playlistData.playlistRecommendationTags)
        ? playlistData.playlistRecommendationTags
        : [];
      const currentTagIds = tags.map(
        (tag: IPlaylistRecommendationTag) => tag.documentId
      );
      const selectedTagIds = selectedTags.map((tag) => tag.documentId);
      return (
        currentTagIds.length !== selectedTagIds.length ||
        currentTagIds.some((id: string) => !selectedTagIds.includes(id))
      );
    }
    return selectedTags.length > 0;
  };

  const onCancelSubmit = () => {
    onClose();
    if (playlistData?.playlistRecommendationTags && memorizedTags.length > 0) {
      const tags = Array.isArray(playlistData.playlistRecommendationTags)
        ? playlistData.playlistRecommendationTags
        : [];
      const normalizedTags = tags
        .map((tag) => {
          return memorizedTags.find(
            (mTag) => mTag.documentId === tag.documentId
          );
        })
        .filter((tag): tag is IPlaylistRecommendationTag => tag !== undefined);
      setSelectedTags(normalizedTags);
    } else {
      setSelectedTags([]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagTitle.trim()) {
      addNotification("Tag title cannot be empty", "error");
      return;
    }

    try {
      await createTagMutation.mutateAsync({
        title: newTagTitle.trim(),
      });
      refetchTags();
      setNewTagTitle("");
      closeModal();
      addNotification("Tag created successfully", "success");
    } catch (error: any) {
      addNotification(
        error?.response?.data?.error?.message || "Failed to create tag",
        "error"
      );
    }
  };

  const toggleCreateModal = () => {
    openModal();
  };

  return (
    <>
      <UIModal
        modalStyles={{
          width: 600,
        }}
        onClose={onClose}
        isOpen={isOpen}
        actions={[
          <UIButton
            key="cancel"
            onClick={onCancelSubmit}
            variant="secondaryOutline"
          >
            Cancel
          </UIButton>,
          <UIButton
            key="submit"
            onClick={() => onConfirm(selectedTags.map((tag) => tag.documentId))}
            variant="primary"
            disabled={
              isTagsLoading ||
              isTagsFetching ||
              loading ||
              !checkIfTagsChanged() ||
              createTagMutation.isPending
            }
          >
            Submit
          </UIButton>,
        ]}
        headerStyles={{
          textAlign: "center",
        }}
        title="Recommendation Tags"
      >
        <ModalContentWrapper style={{ padding: theme.margins.medium }}>
          <SearchBarContainer>
            <SearchInput
              placeholder="Search by tag name"
              {...register("searchText")}
            />
          </SearchBarContainer>
          {isTagsError ? (
            <div>Error loading tags: {tagsError?.message}</div>
          ) : isLoadingPlaylistDetails ||
            (isInitializingRef.current && memorizedTags.length === 0) ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
              }}
            >
              <Spinner />
            </div>
          ) : (
            <MultiSelect<IPlaylistRecommendationTag>
              enableCreate={true}
              toggleCreateModal={toggleCreateModal}
              optionEntityName="tag"
              options={memorizedTags || []}
              labelKey={"title"}
              valueKey={"documentId"}
              setSelected={(tags) => {
                userInteractedRef.current = true;
                setSelectedTags(tags);
              }}
              selected={selectedTags}
              menuItems={[]}
              loading={isTagsLoading || isTagsFetching}
              loaderRef={optionsEndRef}
            />
          )}
        </ModalContentWrapper>
      </UIModal>

      <UIModal
        modalStyles={{
          width: 400,
        }}
        onClose={closeModal}
        isOpen={isCreateModalOpen}
        actions={[
          <UIButton
            key="cancel"
            onClick={closeModal}
            variant="secondaryOutline"
          >
            Cancel
          </UIButton>,
          <UIButton
            key="create"
            onClick={handleCreateTag}
            variant="primary"
            disabled={!newTagTitle.trim() || createTagMutation.isPending}
          >
            Create
          </UIButton>,
        ]}
        title="Add New Tag"
      >
        <div style={{ padding: theme.margins.medium }}>
          <SearchInput
            placeholder="Tag name"
            value={newTagTitle}
            onChange={(e) => setNewTagTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter" && newTagTitle.trim()) {
                handleCreateTag();
              }
            }}
          />
        </div>
      </UIModal>
    </>
  );
};

export default UpdatePlaylistRecommendationTags;
